"use client";

import { Button, DrawerBottom, GridDotsIcon, ShipIcon } from "@pharaday/lighthouse";
import { NamespaceContainerProvider } from "@pharaday/namespace";

import { NavbarAppAndToolMenu } from "../../components";
import { MenuItem } from "../../components/menu-item";
import { AuthenticatedContainer } from "../../core/authenticated.container";
import { mapApplicationTypeToIcon } from "../../core/domain/enums/application-type.enum";
import { mapToolTypeToIcon, ToolTypeEnum } from "../../core/domain/enums/tool-type.enum";
import { useDismissOnNavigate } from "../../hooks/useDismissOnNavigate";
import useFilteredAppListByFeatureFlag from "../../hooks/useFilteredAppListByFeatureFlag";
import useFilteredToolListByFeatureFlag from "../../hooks/useFilteredToolListByFeatureFlag";
import NavigationCommandProvider from "../navbar-header/NavigationCommandProvider";

const formatUrlRoot = (url: string) => (url.startsWith("/") ? url : `/${url}`);

const ApplicationsAndToolsDropdown = () => {
  const { data: apps } = useFilteredAppListByFeatureFlag();
  const { data: tools } = useFilteredToolListByFeatureFlag();
  const [drawerOpen, setDrawerOpen] = useDismissOnNavigate();

  const appData = apps?.map(app => ({ ...app, urlRoot: formatUrlRoot(app.urlRoot) }));

  const toolData = tools
    ?.filter(tool => tool.type !== ToolTypeEnum.ADMIN)
    .map(tool => ({ ...tool, urlRoot: formatUrlRoot(tool.urlRoot) }));

  return (
    <>
      <div className="hidden mobile-screen:block">
        <NavbarAppAndToolMenu appData={appData} toolData={toolData}>
          <Button color="secondary" icon size="md" variant="ghost">
            <GridDotsIcon />
          </Button>
        </NavbarAppAndToolMenu>
      </div>
      <div className="block mobile-screen:hidden">
        <DrawerBottom
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          trigger={
            <Button color="secondary" icon size="md" variant="ghost">
              <GridDotsIcon />
            </Button>
          }
        >
          <div className="mt-3 mb-4 flex justify-center">
            <span className="text-base font-bold text-neutral-10">Apps & Tools</span>
          </div>
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <h3 className="text-sm font-bold text-neutral-10">APPS</h3>
              <div className="grid w-full grid-cols-[repeat(4,6rem)] gap-1 max-[450px]:grid-cols-[repeat(3,6rem)]">
                {appData?.map(app => (
                  <MenuItem
                    key={app.id}
                    Icon={mapApplicationTypeToIcon[app.type] ?? ShipIcon}
                    name={app.name}
                    description={app.description}
                    urlRoot={formatUrlRoot(app.urlRoot)}
                    onClick={() => setDrawerOpen(false)}
                  />
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <h3 className="text-sm font-bold text-neutral-10">TOOLS</h3>
              <div className="grid w-full grid-cols-[repeat(4,6rem)] gap-1 max-[450px]:grid-cols-[repeat(3,6rem)]">
                {toolData?.map(tool => (
                  <MenuItem
                    key={tool.id}
                    Icon={mapToolTypeToIcon[tool.type] ?? ShipIcon}
                    name={tool.name}
                    description={tool.description}
                    urlRoot={formatUrlRoot(tool.urlRoot)}
                    onClick={() => setDrawerOpen(false)}
                  />
                ))}
              </div>
            </div>
          </div>
        </DrawerBottom>
      </div>
    </>
  );
};

export const ApplicationsAndToolsDropdownHOC = () => {
  return (
    <NamespaceContainerProvider container={AuthenticatedContainer}>
      <NavigationCommandProvider>
        <ApplicationsAndToolsDropdown />
      </NavigationCommandProvider>
    </NamespaceContainerProvider>
  );
};

export default ApplicationsAndToolsDropdownHOC;
